import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../hooks/useTheme';
import Card, { CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from './ui/Card';
import Button from './ui/Button';
import { theme } from '../styles/design-system';
import {
  Mic, X, Play, Volume2, Activity, TrendingUp,
  Settings, AlertCircle, Check, ChevronDown, ChevronUp
} from 'lucide-react';

const VoiceAlertModal = ({ isOpen, onClose, onAlertCreated }) => {
  const { user, isAuthenticated } = useAuth();
  const { isDark } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [availableVoices, setAvailableVoices] = useState([]);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [isLoadingPrice, setIsLoadingPrice] = useState(false);
  const [activeSection, setActiveSection] = useState('basic'); // basic, voice, technical

  const [formData, setFormData] = useState({
    symbol: '',
    alertType: 'price_above',
    targetValue: '',
    conditionType: 'above',
    voiceConfig: {
      voiceType: 'female',
      speechRate: 1.0,
      volume: 0.8,
      language: 'en-US',
      customMessage: '',
      enableSound: true,
      enableVoice: true
    },
    technicalConfig: {
      rsiPeriod: 14,
      maShortPeriod: 10,
      maLongPeriod: 20,
      volumeSpikeMultiplier: 2.0,
      volatilityThreshold: 5.0
    },
    userMessage: ''
  });

  const alertTypes = [
    { value: 'price_above', label: 'Price Above', icon: '📈', description: 'Alert when price rises above target' },
    { value: 'price_below', label: 'Price Below', icon: '📉', description: 'Alert when price falls below target' },
    { value: 'price_change_percent', label: 'Price Change %', icon: '📊', description: 'Alert on percentage movement' },
    { value: 'volume_spike', label: 'Volume Spike', icon: '📢', description: 'Unusual volume activity detected' },
    { value: 'rsi_overbought', label: 'RSI Overbought', icon: '🔥', description: 'RSI indicator above 70' },
    { value: 'rsi_oversold', label: 'RSI Oversold', icon: '❄️', description: 'RSI indicator below 30' },
    { value: 'ma_crossover', label: 'MA Crossover', icon: '❌', description: 'Moving average crossover event' },
    { value: 'volatility_high', label: 'High Volatility', icon: '⚡', description: 'High market volatility detected' }
  ];

  useEffect(() => {
    if (isOpen) {
      loadAvailableVoices();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const loadAvailableVoices = async () => {
    try {
      const response = await fetch('/api/voice-alerts/voice/available?language=en-US');
      const data = await response.json();
      if (data.success) {
        setAvailableVoices(data.voices.voices || []);
      }
    } catch (error) {
      console.error('Error loading voices:', error);
    }
  };

  const fetchCurrentPrice = async (symbol) => {
    if (!symbol || symbol.length < 2) {
      setCurrentPrice(null);
      return;
    }

    setIsLoadingPrice(true);
    setCurrentPrice(null);

    try {
      const response = await fetch('/api/chat-command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command: `Get quote for ${symbol}`,
          session_id: 'default'
        })
      });

      const data = await response.json();
      if (data.response && typeof data.response === 'object' && data.response.data) {
        const price = data.response.data.last_price || data.response.data.close || data.response.data.price;
        if (price) {
          setCurrentPrice(parseFloat(price));
        }
      }
    } catch (error) {
      console.error('Error fetching price:', error);
    } finally {
      setIsLoadingPrice(false);
    }
  };

  const handleSymbolChange = (symbol) => {
    const upperSymbol = symbol.toUpperCase();
    setFormData(prev => ({ ...prev, symbol: upperSymbol }));

    if (upperSymbol.length >= 2) {
      setTimeout(() => fetchCurrentPrice(upperSymbol), 500);
    } else {
      setCurrentPrice(null);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleVoiceConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      voiceConfig: {
        ...prev.voiceConfig,
        [field]: value
      }
    }));
  };

  const handleTechnicalConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      technicalConfig: {
        ...prev.technicalConfig,
        [field]: value
      }
    }));
  };

  const handlePreviewVoice = async () => {
    if (!formData.symbol && !formData.voiceConfig.customMessage) {
      // Allow preview even without symbol if custom message is set, or use dummy text
      const dummyText = "This is a preview of your voice alert configuration.";
      speakText(dummyText, formData.voiceConfig);
      return;
    }

    setIsPreviewing(true);
    try {
      const previewText = generatePreviewText();
      speakText(previewText, formData.voiceConfig);
    } catch (error) {
      console.error('Error previewing voice:', error);
    } finally {
      setIsPreviewing(false);
    }
  };

  const generatePreviewText = () => {
    const symbol = formData.symbol || "TEST";
    const targetValue = formData.targetValue || "1000";
    const alertType = formData.alertType;

    if (formData.voiceConfig.customMessage) {
      return formData.voiceConfig.customMessage
        .replace('{symbol}', symbol)
        .replace('{price}', `₹${targetValue}`);
    }

    switch (alertType) {
      case 'price_above': return `Price alert: ${symbol} has reached ₹${targetValue}`;
      case 'price_below': return `Price alert: ${symbol} has dropped to ₹${targetValue}`;
      case 'price_change_percent': return `Price change alert: ${symbol} moved ${targetValue}%`;
      case 'volume_spike': return `Volume spike alert: ${symbol} has unusual volume activity`;
      case 'rsi_overbought': return `RSI alert: ${symbol} is overbought`;
      case 'rsi_oversold': return `RSI alert: ${symbol} is oversold`;
      case 'ma_crossover': return `Moving average alert: ${symbol} crossover detected`;
      case 'volatility_high': return `Volatility alert: ${symbol} showing high volatility`;
      default: return `Alert: ${symbol} condition met`;
    }
  };

  const speakText = (text, voiceConfig) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = voiceConfig.speechRate;
      utterance.pitch = 1.0;
      utterance.volume = voiceConfig.volume;
      utterance.lang = voiceConfig.language;

      const voices = speechSynthesis.getVoices();
      const selectedVoice = voices.find(voice =>
        voice.name.toLowerCase().includes(voiceConfig.voiceType.toLowerCase())
      );
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }

      speechSynthesis.speak(utterance);
    } else {
      alert('Speech synthesis not supported in this browser');
    }
  };

  const handleSubmit = async () => {
    if (!isAuthenticated || !user?.id) {
      alert('Please login to create voice alerts');
      return;
    }

    if (!formData.symbol || !formData.targetValue) {
      alert('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch('/api/voice-alerts/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id.toString(),
          symbol: formData.symbol,
          alert_type: formData.alertType,
          target_value: parseFloat(formData.targetValue),
          condition_type: formData.conditionType,
          voice_config: formData.voiceConfig,
          technical_config: formData.technicalConfig,
          user_message: formData.userMessage
        })
      });

      const data = await response.json();
      if (data.success) {
        onAlertCreated && onAlertCreated(data.alert_data);
        handleClose();
      } else {
        alert(`Error: ${data.message}`);
      }
    } catch (error) {
      console.error('Error creating voice alert:', error);
      alert('Error creating voice alert. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      symbol: '',
      alertType: 'price_above',
      targetValue: '',
      conditionType: 'above',
      voiceConfig: {
        voiceType: 'female',
        speechRate: 1.0,
        volume: 0.8,
        language: 'en-US',
        customMessage: '',
        enableSound: true,
        enableVoice: true
      },
      technicalConfig: {
        rsiPeriod: 14,
        maShortPeriod: 10,
        maLongPeriod: 20,
        volumeSpikeMultiplier: 2.0,
        volatilityThreshold: 5.0
      },
      userMessage: ''
    });
    setCurrentPrice(null);
    setActiveSection('basic');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.backdrop} onClick={handleClose} />
      <div style={styles.modalContainer}>
        <div style={styles.modalContent}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerIcon}>
              <Mic size={24} color="white" />
            </div>
            <div style={styles.headerText}>
              <h2 style={styles.title}>Create Voice Alert</h2>
              <p style={styles.subtitle}>Configure intelligent voice notifications for market events</p>
            </div>
            <button style={styles.closeButton} onClick={handleClose}>
              <X size={20} />
            </button>
          </div>

          <div style={styles.body}>
            {/* Navigation Tabs */}
            <div style={styles.tabs}>
              <button
                style={activeSection === 'basic' ? styles.tabActive : styles.tab}
                onClick={() => setActiveSection('basic')}
              >
                <Activity size={16} /> Basic Setup
              </button>
              <button
                style={activeSection === 'voice' ? styles.tabActive : styles.tab}
                onClick={() => setActiveSection('voice')}
              >
                <Volume2 size={16} /> Voice Settings
              </button>
              <button
                style={activeSection === 'technical' ? styles.tabActive : styles.tab}
                onClick={() => setActiveSection('technical')}
              >
                <Settings size={16} /> Advanced
              </button>
            </div>

            {/* Content Area */}
            <div style={styles.contentScroll}>

              {/* BASIC SETUP SECTION */}
              {activeSection === 'basic' && (
                <div style={styles.sectionContent}>
                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Stock Symbol</label>
                    <div style={styles.symbolWrapper}>
                      <input
                        type="text"
                        value={formData.symbol}
                        onChange={(e) => handleSymbolChange(e.target.value)}
                        placeholder="e.g. RELIANCE"
                        style={styles.inputLarge}
                        autoFocus
                      />
                      {isLoadingPrice && <div style={styles.loaderSmall} />}
                    </div>
                    {currentPrice && (
                      <div style={styles.priceTag}>
                        <span>Current Price: <strong>₹{currentPrice.toFixed(2)}</strong></span>
                        <button
                          style={styles.usePriceBtn}
                          onClick={() => setFormData(prev => ({ ...prev, targetValue: currentPrice.toFixed(2) }))}
                        >
                          Use This
                        </button>
                      </div>
                    )}
                  </div>

                  <div style={styles.grid2}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Alert Condition</label>
                      <div style={styles.selectWrapper}>
                        <select
                          value={formData.alertType}
                          onChange={(e) => handleInputChange('alertType', e.target.value)}
                          style={styles.select}
                        >
                          {alertTypes.map(type => (
                            <option key={type.value} value={type.value}>
                              {type.icon} {type.label}
                            </option>
                          ))}
                        </select>
                        <ChevronDown size={16} style={styles.selectIcon} />
                      </div>
                      <p style={styles.helperText}>
                        {alertTypes.find(t => t.value === formData.alertType)?.description}
                      </p>
                    </div>

                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Target Value</label>
                      <input
                        type="number"
                        value={formData.targetValue}
                        onChange={(e) => handleInputChange('targetValue', e.target.value)}
                        placeholder="0.00"
                        style={styles.input}
                        step="0.05"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* VOICE SETTINGS SECTION */}
              {activeSection === 'voice' && (
                <div style={styles.sectionContent}>
                  <div style={styles.voicePreviewCard}>
                    <div style={styles.voiceControls}>
                      <button
                        style={styles.previewBtn}
                        onClick={handlePreviewVoice}
                        disabled={isPreviewing}
                      >
                        {isPreviewing ? <Activity className="animate-pulse" size={20} /> : <Play size={20} />}
                        {isPreviewing ? 'Playing...' : 'Test Voice'}
                      </button>
                      <div style={styles.volumeControl}>
                        <Volume2 size={16} color="#6b7280" />
                        <input
                          type="range"
                          min="0"
                          max="1"
                          step="0.1"
                          value={formData.voiceConfig.volume}
                          onChange={(e) => handleVoiceConfigChange('volume', parseFloat(e.target.value))}
                          style={styles.slider}
                        />
                      </div>
                    </div>
                  </div>

                  <div style={styles.grid2}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Voice Persona</label>
                      <div style={styles.selectWrapper}>
                        <select
                          value={formData.voiceConfig.voiceType}
                          onChange={(e) => handleVoiceConfigChange('voiceType', e.target.value)}
                          style={styles.select}
                        >
                          {availableVoices.map(voice => (
                            <option key={voice.id} value={voice.id}>{voice.name}</option>
                          ))}
                          <option value="female">Default Female</option>
                          <option value="male">Default Male</option>
                        </select>
                        <ChevronDown size={16} style={styles.selectIcon} />
                      </div>
                    </div>

                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Speech Rate ({formData.voiceConfig.speechRate}x)</label>
                      <input
                        type="range"
                        min="0.5"
                        max="2.0"
                        step="0.1"
                        value={formData.voiceConfig.speechRate}
                        onChange={(e) => handleVoiceConfigChange('speechRate', parseFloat(e.target.value))}
                        style={styles.slider}
                      />
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Custom Message Template</label>
                    <textarea
                      value={formData.voiceConfig.customMessage}
                      onChange={(e) => handleVoiceConfigChange('customMessage', e.target.value)}
                      placeholder="Alert! {symbol} just hit {price}. Check it out."
                      style={styles.textarea}
                      rows={3}
                    />
                    <p style={styles.helperText}>Use <code>{'{symbol}'}</code> and <code>{'{price}'}</code> as placeholders.</p>
                  </div>

                  <div style={styles.toggles}>
                    <label style={styles.toggleLabel}>
                      <input
                        type="checkbox"
                        checked={formData.voiceConfig.enableVoice}
                        onChange={(e) => handleVoiceConfigChange('enableVoice', e.target.checked)}
                        style={styles.checkbox}
                      />
                      <span>Enable Voice Announcement</span>
                    </label>
                  </div>
                </div>
              )}

              {/* TECHNICAL SETTINGS SECTION */}
              {activeSection === 'technical' && (
                <div style={styles.sectionContent}>
                  <div style={styles.infoBox}>
                    <AlertCircle size={16} />
                    <p>These settings fine-tune the trigger conditions for technical alerts like RSI and Moving Averages.</p>
                  </div>

                  <div style={styles.grid2}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>RSI Period</label>
                      <input
                        type="number"
                        value={formData.technicalConfig.rsiPeriod}
                        onChange={(e) => handleTechnicalConfigChange('rsiPeriod', parseInt(e.target.value))}
                        style={styles.input}
                      />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Volume Multiplier</label>
                      <input
                        type="number"
                        value={formData.technicalConfig.volumeSpikeMultiplier}
                        onChange={(e) => handleTechnicalConfigChange('volumeSpikeMultiplier', parseFloat(e.target.value))}
                        style={styles.input}
                        step="0.1"
                      />
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button style={styles.cancelBtn} onClick={handleClose}>Cancel</button>
            <button
              style={styles.createBtn}
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Creating...' : 'Create Alert'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed',
    top: 0, left: 0, right: 0, bottom: 0,
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px'
  },
  backdrop: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
  },
  modalContainer: {
    position: 'relative',
    width: '100%',
    maxWidth: '600px',
    backgroundColor: '#ffffff',
    borderRadius: '24px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    animation: 'slideUp 0.3s ease-out',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column'
  },
  header: {
    padding: '24px',
    background: 'linear-gradient(135deg, #4f46e5 0%, #3b82f6 100%)',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    position: 'relative'
  },
  headerIcon: {
    width: '48px',
    height: '48px',
    borderRadius: '16px',
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backdropFilter: 'blur(4px)'
  },
  headerText: {
    flex: 1
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    letterSpacing: '-0.025em'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    opacity: 0.9,
    fontWeight: '400'
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: 'white',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background 0.2s',
    ':hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)'
    }
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    overflow: 'hidden'
  },
  tabs: {
    display: 'flex',
    padding: '16px 24px 0',
    gap: '8px',
    borderBottom: '1px solid #e5e7eb',
    backgroundColor: '#f9fafb'
  },
  tab: {
    padding: '12px 16px',
    border: 'none',
    background: 'transparent',
    color: '#6b7280',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s'
  },
  tabActive: {
    padding: '12px 16px',
    border: 'none',
    background: 'transparent',
    color: '#4f46e5',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: '2px solid #4f46e5'
  },
  contentScroll: {
    padding: '24px',
    overflowY: 'auto',
    flex: 1
  },
  sectionContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  input: {
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    transition: 'all 0.2s',
    outline: 'none',
    width: '100%',
    ':focus': {
      borderColor: '#4f46e5',
      boxShadow: '0 0 0 3px rgba(79, 70, 229, 0.1)'
    }
  },
  inputLarge: {
    padding: '16px',
    borderRadius: '16px',
    border: '2px solid #e5e7eb',
    fontSize: '24px',
    fontWeight: '700',
    textAlign: 'center',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    outline: 'none',
    width: '100%',
    transition: 'all 0.2s'
  },
  symbolWrapper: {
    position: 'relative'
  },
  loaderSmall: {
    position: 'absolute',
    right: '16px',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '20px',
    height: '20px',
    border: '2px solid #e5e7eb',
    borderTopColor: '#4f46e5',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  },
  priceTag: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#eff6ff',
    padding: '8px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    color: '#1e40af'
  },
  usePriceBtn: {
    background: 'white',
    border: '1px solid #bfdbfe',
    color: '#2563eb',
    padding: '4px 8px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  grid2: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px'
  },
  selectWrapper: {
    position: 'relative'
  },
  select: {
    appearance: 'none',
    width: '100%',
    padding: '12px 16px',
    paddingRight: '40px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    fontSize: '15px',
    backgroundColor: 'white',
    outline: 'none',
    cursor: 'pointer'
  },
  selectIcon: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
    color: '#6b7280'
  },
  helperText: {
    fontSize: '12px',
    color: '#6b7280',
    marginTop: '4px'
  },
  voicePreviewCard: {
    backgroundColor: '#f3f4f6',
    borderRadius: '16px',
    padding: '20px',
    marginBottom: '8px'
  },
  voiceControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px'
  },
  previewBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'transform 0.1s',
    ':active': { transform: 'scale(0.98)' }
  },
  volumeControl: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  slider: {
    flex: 1,
    height: '6px',
    borderRadius: '3px',
    background: '#d1d5db',
    outline: 'none',
    cursor: 'pointer'
  },
  textarea: {
    width: '100%',
    padding: '12px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    fontSize: '14px',
    resize: 'vertical',
    outline: 'none',
    fontFamily: 'inherit'
  },
  toggles: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  toggleLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151'
  },
  checkbox: {
    width: '18px',
    height: '18px',
    accentColor: '#4f46e5',
    cursor: 'pointer'
  },
  infoBox: {
    backgroundColor: '#eff6ff',
    border: '1px solid #dbeafe',
    borderRadius: '12px',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    color: '#1e40af',
    fontSize: '14px',
    lineHeight: '1.5'
  },
  footer: {
    padding: '20px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    backgroundColor: '#f9fafb'
  },
  cancelBtn: {
    padding: '12px 24px',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    backgroundColor: 'white',
    color: '#374151',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'all 0.2s',
    ':hover': { backgroundColor: '#f3f4f6' }
  },
  createBtn: {
    padding: '12px 24px',
    borderRadius: '12px',
    border: 'none',
    backgroundColor: '#4f46e5',
    color: 'white',
    fontWeight: '600',
    cursor: 'pointer',
    fontSize: '14px',
    boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)',
    transition: 'all 0.2s',
    ':hover': { backgroundColor: '#4338ca' },
    ':disabled': { opacity: 0.7, cursor: 'not-allowed' }
  }
};

export default VoiceAlertModal;
